















import { defineComponent, ref, onMounted } from "@vue/composition-api";
import { ButtonOption, InfoOption } from "../components/LandCard.vue";
import ListAndTabUI, { TabItem } from "../components/ListAndTabUI.vue";
import {
  calculateCenter,
  GraphicInfo,
  lngLatListToLatLngList,
} from "@tuliu/base-map";
import { JSCallAPP } from "@/utils/interactive";
import { DRAW_MODEL } from "@/constants/drawModel";
import {
  getLandListByType,
  getStatefulLandListByType,
  LandInfo,
  StatefulLandInfo,
} from "@/api/land";
import { eventHub } from "@/utils/eventHub";
import { useDrawStore } from "@/store/draw";

enum Status {
  draft = 0, // 草稿（未补贴）
  underReview = 1, // 审核中
  fail = 2, // 审核不通过
  pass = 3, // 待补贴
  subsidized = 4, // 已发放
}

const getStatusText = (s: Status) => {
  switch (s) {
    case Status.underReview:
      return "审核中";
    case Status.fail:
      return "审核不通过";
    case Status.pass:
      return "待发放";
    case Status.subsidized:
      return "已补贴";
    default:
      return "";
  }
};

const getColor = (s: Status) => {
  return s === Status.subsidized
    ? // 已奖励
      "#76B345"
    : // 未奖励
      "#FF8300";
};

const default_weight = 2;

const getButtonList = (
  status: Status,
  id: number,
  tid: number,
  locationId: number,
  hasBusinessInfo: number,
  hasTransferInfo: number
) => {
  const handleClick = (action: string) => {
    return () => {
      JSCallAPP({
        method: "enclosure_action",
        data: {
          action,
          id,
          tid,
          locationId,
          hasBusinessInfo,
          hasTransferInfo,
        },
      });
    };
  };

  const receive_subsidy = {
    text: "完善信息领补贴",
    type: "primary",
    onClick: handleClick("receive_subsidy"),
  };
  const edit = {
    text: "去修改",
    type: "primary",
    onClick: handleClick("edit"),
  };
  const view_detail = {
    text: "查看信息",
    type: "primary",
    onClick: handleClick("view_detail"),
  };

  const statusButtonMap: Record<Status, ButtonOption[]> = {
    [Status.draft]: [receive_subsidy],
    [Status.underReview]: [view_detail],
    [Status.fail]: [edit],
    [Status.pass]: [view_detail],
    [Status.subsidized]: [view_detail],
  };

  return statusButtonMap[status];
};

/**
 * 圈地赚钱页面
 */
export default defineComponent({
  components: { ListAndTabUI },
  setup() {
    const store = useDrawStore();

    /**
     * 地块详情相关逻辑
     */
    const graphicInfoList = ref<GraphicInfo[]>([]);
    let detailList: InfoOption[] = [];
    const detailInfo = ref<InfoOption>();

    onMounted(() => {
      // 当前选中地块变化时更新信息
      eventHub.$on("set-active-graphic", (id: number) => {
        const index = detailList.findIndex((item) => item.id === id);
        if (index !== -1) {
          detailInfo.value = detailList[index];
        }
      });
    });

    const getMapLandList = async () => {
      const [err, data] = await getLandListByType(1);

      // 需要判断下当前编辑模式
      // 如果用户点击了开始绘制，则把数据丢弃
      if (
        !err &&
        data !== undefined &&
        store.drawModel === DRAW_MODEL.readToCreate
      ) {
        // 后端返回的数据第一个作为选中的数据
        // 但是绘制图形时为了保证选中的地块显示在最上面，把数据反转一下
        let localData = data
          .filter((i) => i.coordinates)
          .sort((i) => (i.allowanceStatus === Status.draft ? 1 : -1));
        const lastIndex = localData.length - 1;

        detailList = localData.map((item) => {
          return {
            ...item,
            statusText: getStatusText(item.allowanceStatus!),
            buttonList: getButtonList(
              item.allowanceStatus!,
              item.id,
              item.tid,
              item.locationId,
              item.hasBusinessInfo!,
              item.hasTransferInfo!
            ),
          };
        });

        graphicInfoList.value = localData.map((item) => {
          const themeColor = getColor(item.allowanceStatus!);
          return {
            id: item.id,
            path: lngLatListToLatLngList(JSON.parse(item.coordinates!)),
            isClosed: true,
            fillColor: themeColor,
            themeColor: themeColor,
            weight: default_weight,
            title: item.allowanceStatus === Status.draft ? "拿补贴" : "",
            editorEnable: false,
            extInfo: {
              themeColor: themeColor,
              weight: default_weight,
            },
          };
        });

        if (localData.length === 0) {
          store.drawModel = DRAW_MODEL.create;
        } else {
          store.mapCenter = calculateCenter(
            graphicInfoList.value[lastIndex].path
          )!;
          store.graphicInfoList = graphicInfoList.value;
          eventHub.$emit(
            "set-active-graphic",
            graphicInfoList.value[lastIndex].id
          );
        }
      }
    };

    onMounted(() => {
      getMapLandList();

      // 点击地图地块上的标题
      eventHub.$on("map-title-click", (id: number) => {
        const item = detailList.find((i) => i.id === id)!;
        JSCallAPP({
          method: "enclosure_action",
          data: {
            action: "receive_subsidy",
            id,
            tid: item.tid,
            locationId: item.locationId,
          },
        });
      });
    });

    /**
     * 列表相关逻辑
     */
    const tabList = ref<
      (TabItem & { type: number; infoList: InfoOption<StatefulLandInfo>[] })[]
    >(
      new Array(2).fill(null).map((i, index) => ({
        infoList: [],
        refreshing: false,
        isInit: false,
        finished: false,
        loading: false,
        err: false,
        page: 1,
        pageSize: 10,
        type: index === 0 ? 1 : 2,
      }))
    );

    const handleLoad = async (tabIndex: number, resetData: boolean) => {
      const { page, pageSize, type } = tabList.value[tabIndex];
      const [err, data] = await getStatefulLandListByType({
        page,
        size: pageSize,
        type,
      });

      if (!err && data !== undefined && data.data) {
        tabList.value[tabIndex].infoList = [
          ...(resetData ? [] : tabList.value[tabIndex].infoList),
          ...data.data.map((item) => {
            return {
              ...item,
              statusText: getStatusText(item.allowanceStatus!),
              buttonList: getButtonList(
                item.allowanceStatus!,
                item.id,
                item.tid,
                item.locationId,
                item.hasBusinessInfo!,
                item.hasTransferInfo!
              ),
            };
          }),
        ];

        // 列表第一次初始化时，定位到列表的第一块数据
        if (
          !tabList.value[tabIndex].isInit &&
          data.data &&
          data.data.length > 0
        ) {
          eventHub.$emit("set-active-graphic", data.data[0].id);
        }

        tabList.value[tabIndex].finished =
          data.maxPageNumber <= tabList.value[tabIndex].page;
        tabList.value[tabIndex].page = tabList.value[tabIndex].page + 1;
      } else {
        tabList.value[tabIndex].err = true;
      }

      tabList.value[tabIndex].isInit = true;
      tabList.value[tabIndex].loading = false;
    };

    const handleToExplanation = () => {
      JSCallAPP({
        method: "to_explanation",
        data: 1,
      });
    };

    const handleToDetail = (landInfo: LandInfo) => {
      JSCallAPP({
        method: "enclosure_action",
        data: {
          action: "view_land_detail",
          id: landInfo.id,
          tid: landInfo.tid,
          locationId: landInfo.locationId,
        },
      });
    };

    return {
      store,
      handleToDetail,
      handleToExplanation,
      handleLoad,
      tabList,
      detailInfo,
    };
  },
});
